import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  BookOpen,
  CircleSlash,
  CornerDownLeft,
  ExternalLink,
  GitPullRequest,
  Laptop,
  LayoutDashboard,
  LayoutGrid,
  ListTodo,
  type LucideIcon,
  Moon,
  OctagonX,
  Plus,
  RefreshCw,
  Rows3,
  ScrollText,
  Search,
  Send,
  Server,
  Settings,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { useSession } from '@/components/auth/session';
import { prNumber } from '@/components/common/bits';
import { useDensity } from '@/components/layout/DensityProvider';
import { useShell } from '@/components/layout/shell';
import { ASK, type AskConfig } from '@/features/actions/verbs';
import {
  useBriefs,
  useProjects,
  useReview,
  useTasks,
  useUsers,
  useWorkers,
} from '@/hooks/use-fleet';
import { api } from '@/lib/api';
import { briefsAwaitingPlan, isCancellable } from '@/lib/briefs';
import { labelForKind } from '@/lib/event-kinds';
import { failures24h, heldByWorker, planApprovals, presenceOf } from '@/lib/fleet';
import { fuzzyMatch } from '@/lib/fuzzy';
import { qualifiedKey } from '@/lib/keys';
import { LIVE_KEYS } from '@/lib/query';
import { useBodyScrollLock } from '@/lib/scroll-lock';
import { cn } from '@/lib/utils';

/**
 * The command palette.
 *
 * This is the one ambitious moment in the design, and what makes it worth the
 * ambition is that it is not only a router. Every gate in the product is in
 * here: the plans waiting for approval, the pull requests waiting for a
 * decision, the failures waiting for a retry -- each with its verbs, each able
 * to ask its question and write, without the page underneath changing. So the
 * fastest way to clear what is waiting on you is to press one key and type.
 *
 * Three ideas hold it together.
 *
 * 1. **Everything is one item type.** A view, a task, a project and "send back
 *    T-4 with a note" are all a `Command`: a label, a haystack and something to
 *    do. That is what lets one fuzzy pass rank a destination against an action
 *    and produce a single sane list.
 * 2. **A verb that needs a reason asks inside the palette.** Choosing "Send
 *    back" swaps the search field for the reason field, keeps the question
 *    above it, and Enter writes. It is the same question `AskBox` asks in a row,
 *    word for word, from the same table -- one copy of the copy.
 * 3. **The resting state is the gates.** With an empty query the first group is
 *    what is waiting on a person, oldest first, so opening the palette and
 *    pressing Enter takes you to the thing you should look at next.
 * 4. **A verb is reached through the thing it acts on.** Every gate carries two
 *    to four verbs, so ten gates is forty verb rows, and a list of forty verbs
 *    is not a list of ten decisions. The verbs are one keystroke behind their
 *    row instead: right arrow (or cmd-Enter) on a task or a brief lists what can
 *    be done to it, escape or left arrow comes back, and Enter still opens the
 *    thing rather than acting on it. Typing still searches the verbs directly,
 *    because somebody who types "approve" has already named what they want.
 */
type Group =
  | 'Waiting on you'
  | 'Jump to'
  | 'Tasks'
  | 'Jobs'
  | 'Projects'
  | 'People'
  | 'Devices'
  | 'Actions'
  | 'Display';

/**
 * The thing before what can be done to it.
 *
 * `Actions` used to sit second, so typing a brief's name listed "Approve plan -
 * ..." and "Reject plan - ..." above the brief itself, and Enter on the first row
 * was a decision rather than a look. A person searching by name is looking for
 * the thing; the verbs are what they choose once they have found it.
 */
const GROUP_ORDER: Group[] = [
  'Waiting on you',
  'Jump to',
  'Tasks',
  'Jobs',
  'Projects',
  'People',
  'Devices',
  'Actions',
  'Display',
];

interface Command {
  id: string;
  group: Group;
  label: string;
  /** Quiet trailing context on the same line: a repo, a state, a worker. */
  detail?: string;
  /** Right-aligned meta: an age, a count, a keystroke. */
  hint?: string;
  icon: LucideIcon;
  tone?: 'gate' | 'danger';
  /** The text the matcher sees. */
  search: string;
  /**
   * The brief or task this row is about. An entity row and every verb that acts
   * on it carry the same one, which is what lets the palette show a thing's verbs
   * without listing every verb in the product beside it.
   */
  subject?: string;
  /** When set, the palette asks this question before it writes. */
  ask?: AskConfig;
  /** What the verb is about, for the reason step's header. */
  target?: string;
  perform: (value: string) => void | Promise<void>;
}

function Row({
  command,
  active,
  hasVerbs,
  onHover,
  onPick,
  id,
}: {
  command: Command;
  active: boolean;
  /** This row is a thing with verbs behind it, one arrow key away. */
  hasVerbs?: boolean;
  onHover: () => void;
  onPick: () => void;
  id: string;
}) {
  const Icon = command.icon;
  return (
    // A real button: focus stays in the search field and the listbox is driven by
    // aria-activedescendant, but the row still answers a click and a tap the way
    // every other control in the app does.
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onMouseMove={onHover}
      onClick={onPick}
      className={cn(
        'relative flex h-9 w-full cursor-pointer items-center gap-2.5 px-3 text-left text-sm max-sm:h-11',
        active ? 'bg-raised' : 'bg-transparent',
      )}
    >
      {active ? (
        <span className="bg-gate absolute inset-y-1 left-0 w-[2px] rounded-r-sm" aria-hidden />
      ) : null}
      <Icon
        className={cn(
          'size-[14px] shrink-0',
          command.tone === 'gate'
            ? 'text-gate'
            : command.tone === 'danger'
              ? 'text-bad'
              : 'text-fg-3',
        )}
        aria-hidden
      />
      <span
        className={cn(
          'min-w-0 truncate',
          active ? 'text-foreground' : 'text-fg-2',
          command.tone === 'danger' && 'text-bad',
        )}
      >
        {command.label}
      </span>
      {command.detail ? (
        <span className="text-fg-3 min-w-0 shrink truncate text-xs">{command.detail}</span>
      ) : null}
      <span className="ml-auto flex shrink-0 items-center gap-2">
        {command.hint ? <span className="text-fg-3 tnum text-2xs">{command.hint}</span> : null}
        {active && hasVerbs ? <ArrowRight className="text-fg-3 size-3" aria-hidden /> : null}
        {active ? <CornerDownLeft className="text-fg-3 size-3" aria-hidden /> : null}
      </span>
    </button>
  );
}

function Legend({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('text-fg-3 flex items-center gap-1 text-2xs', className)}>{children}</span>
  );
}

export function CommandPalette() {
  const { paletteOpen, closePalette, openCompose } = useShell();
  const { session } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTheme, resolvedTheme } = useTheme();
  const { density, toggleDensity } = useDensity();

  // Nothing is asked for while the password is temporary; the hub would refuse
  // it, and the palette has nowhere to send the person but the account page.
  const unlocked = !session.mustChangePassword;
  const briefs = useBriefs(unlocked);
  const tasks = useTasks(unlocked);
  const review = useReview(unlocked);
  const projects = useProjects(unlocked);
  /**
   * People and the device list are admin reads on the server, so a member's
   * palette does not ask for them and does not offer what it cannot open. They
   * also wait for the palette to be opened, unlike the four above: those are what
   * the pages behind are drawing anyway, while `users` is read by nothing else on
   * most routes and would otherwise be a request every thirty seconds for a
   * palette nobody has opened. react-query keeps what it fetched, so it is one
   * wait, once.
   */
  const people = useUsers(session.isAdmin && paletteOpen && unlocked);
  const devices = useWorkers(session.isAdmin && paletteOpen && unlocked);

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  /**
   * The thing whose verbs are being shown, by subject id rather than by the row
   * object: the command list is rebuilt whenever the fleet moves, and a held row
   * would go on offering a verb the hub has since refused.
   */
  const [drilled, setDrilled] = useState<string | null>(null);
  /**
   * The search the drill interrupted. Entering the verbs empties the field so
   * they are all on screen and typing narrows them; leaving puts back what was
   * being searched for, because arriving at a thing and changing your mind
   * should not also lose the query that found it.
   */
  const [interrupted, setInterrupted] = useState('');
  const [pending, setPending] = useState<Command | null>(null);
  /** `pending`, readable from a closure that was built before it was set. */
  const asking = useRef(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const reset = useCallback(() => {
    setQuery('');
    setActive(0);
    setDrilled(null);
    setInterrupted('');
    setPending(null);
    setValue('');
    setError(null);
    setBusy(false);
  }, []);

  const close = useCallback(() => {
    closePalette();
    reset();
  }, [closePalette, reset]);

  useEffect(() => {
    asking.current = pending !== null;
  });

  /** Runs a write, refreshes the live set, and closes on success. */
  const write = useCallback(
    async (thunk: () => Promise<unknown>, success: string) => {
      setBusy(true);
      setError(null);
      try {
        await thunk();
        for (const key of LIVE_KEYS) void queryClient.invalidateQueries({ queryKey: key });
        toast.success(success);
        close();
      } catch (failure) {
        const message = failure instanceof Error ? failure.message : 'that did not work';
        setError(message);
        // The question step prints this under its own field, so a toast there is
        // the same sentence twice (`inlineError` in `useHubAction` is the same
        // rule). A verb that asked nothing has nowhere to print it, and the
        // toast is the only thing that would say the write failed at all.
        //
        // Through a ref, not the state: a command's `perform` is built once and
        // then held in `pending` itself, so the closure that runs here captured
        // the world as it was before the question opened.
        if (!asking.current) toast.error(message);
      } finally {
        setBusy(false);
      }
    },
    [close, queryClient],
  );

  /** Open a thing's verbs, keeping the search that found it for the way back. */
  const enterVerbs = useCallback((subject: string, from: string) => {
    setInterrupted(from);
    setQuery('');
    setDrilled(subject);
    setActive(0);
  }, []);

  const leaveVerbs = useCallback(() => {
    setDrilled(null);
    setQuery(interrupted);
    setInterrupted('');
    setActive(0);
  }, [interrupted]);

  const go = useCallback(
    (to: string, params?: Record<string, string>, search?: Record<string, unknown>) => {
      // biome-ignore lint/suspicious/noExplicitAny: the palette holds paths as data, which the typed router cannot narrow.
      void navigate({ to, params, search } as any);
      close();
    },
    [close, navigate],
  );

  const commands = useMemo<Command[]>(() => {
    if (!paletteOpen) return [];

    const list: Command[] = [];
    const gateBriefs = planApprovals(briefs.data ?? []);
    const queue = review.data?.queue ?? [];
    const failed = failures24h(tasks.data ?? []);
    const taskRows = tasks.data ?? [];
    const unplanned = briefsAwaitingPlan(briefs.data ?? [], taskRows);

    // --- what is waiting on a person, oldest first -------------------------
    for (const brief of gateBriefs) {
      list.push({
        id: `gate-brief-${brief.id}`,
        group: 'Waiting on you',
        subject: brief.id,
        label: brief.title,
        detail: `plan · ${brief.repos[0] ?? 'no repo'}`,
        icon: ShieldCheck,
        tone: 'gate',
        search: `${brief.title} ${brief.repos.join(' ')} ${brief.submittedBy} plan approval gate job brief`,
        perform: () => go('/jobs/$briefId', { briefId: brief.id }),
      });
    }
    for (const entry of queue) {
      list.push({
        id: `gate-task-${entry.id}`,
        group: 'Waiting on you',
        subject: entry.id,
        label: entry.title,
        detail: `${qualifiedKey(entry.briefId, entry.key)} · PR review · ${entry.repo}`,
        hint: entry.stale ? 'stale' : undefined,
        icon: GitPullRequest,
        tone: 'gate',
        search: `${qualifiedKey(entry.briefId, entry.key)} ${entry.title} ${entry.repo} ${
          entry.briefTitle
        } ${entry.workerName ?? ''} ${entry.ownerName ?? ''} ${
          entry.ownerEmail ?? ''
        } pull request review gate`,
        perform: () => go('/tasks/$taskId', { taskId: entry.id }),
      });
    }

    // --- the verbs, one per gate ------------------------------------------
    for (const brief of gateBriefs) {
      list.push({
        id: `approve-${brief.id}`,
        group: 'Actions',
        subject: brief.id,
        label: `Approve plan - ${brief.title}`,
        detail: brief.repos[0] ?? undefined,
        icon: ShieldCheck,
        tone: 'gate',
        search: `approve go plan dispatch ${brief.title}`,
        ask: ASK.approve,
        target: brief.title,
        perform: () => write(() => api.approveBrief(brief.id), 'Plan approved. Tasks queued.'),
      });
      list.push({
        id: `reject-${brief.id}`,
        group: 'Actions',
        subject: brief.id,
        label: `Reject plan - ${brief.title}`,
        icon: CircleSlash,
        tone: 'danger',
        search: `reject plan ${brief.title}`,
        ask: ASK.reject,
        target: brief.title,
        perform: (reason) =>
          write(() => api.rejectBrief(brief.id, reason || undefined), 'Plan rejected.'),
      });
    }
    for (const entry of queue) {
      list.push({
        id: `merge-${entry.id}`,
        group: 'Actions',
        subject: entry.id,
        label: `Mark merged - ${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        icon: GitPullRequest,
        tone: 'gate',
        search: `mark merged done ship ${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        // The same question the row asks. A verb reached by typing three letters
        // and pressing Enter is the last place a gate should close unasked.
        ask: ASK.markMerged,
        target: `${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        perform: () => write(() => api.markDone(entry.id), 'Marked merged.'),
      });
      list.push({
        id: `changes-${entry.id}`,
        group: 'Actions',
        subject: entry.id,
        label: `Send back - ${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        icon: Send,
        search: `send back changes requested rework ${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        ask: ASK.changes,
        target: `${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        perform: (feedback) =>
          write(
            () => api.requeueTask(entry.id, { feedback: feedback || undefined }),
            'Sent back with your note.',
          ),
      });
      list.push({
        id: `reassign-${entry.id}`,
        group: 'Actions',
        subject: entry.id,
        label: `Reassign - ${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        icon: RefreshCw,
        search: `reassign another device worker ${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        ask: ASK.reassign,
        target: `${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
        perform: (note) =>
          write(
            () => api.requeueTask(entry.id, { reassign: true, feedback: note || undefined }),
            'Reassigned to another device.',
          ),
      });
      if (entry.prUrl) {
        const url = entry.prUrl;
        list.push({
          id: `pr-${entry.id}`,
          group: 'Actions',
          subject: entry.id,
          label: `Open PR${prNumber(url)} - ${entry.title}`,
          icon: ExternalLink,
          search: `open pull request pr github ${qualifiedKey(entry.briefId, entry.key)} ${entry.title}`,
          perform: () => {
            window.open(url, '_blank', 'noreferrer');
            close();
          },
        });
      }
    }
    // A draft nobody is planning: the one verb that gets it moving. Offered only
    // on the briefs the hub would accept it for; see `awaitsPlan`.
    for (const brief of unplanned) {
      list.push({
        id: `plan-${brief.id}`,
        group: 'Actions',
        subject: brief.id,
        label: `Request plan - ${brief.title}`,
        detail: brief.repos[0] ?? undefined,
        icon: Sparkles,
        tone: 'gate',
        search: `request plan decompose draft ${brief.title} ${brief.repos.join(' ')}`,
        perform: () =>
          write(
            () => api.requestPlan(brief.id),
            'Plan requested. A worker picks it up on the next sweep.',
          ),
      });
    }
    // The pull cord on work already dispatched. Only approved briefs have it: the
    // hub refuses a cancellation anywhere else, and refuses a rejection here.
    for (const brief of briefs.data ?? []) {
      if (!isCancellable(brief)) continue;
      list.push({
        id: `cancel-brief-${brief.id}`,
        group: 'Actions',
        subject: brief.id,
        label: `Cancel job - ${brief.title}`,
        detail: brief.repos[0] ?? undefined,
        icon: OctagonX,
        tone: 'danger',
        search: `cancel job brief stop halt abandon ${brief.title} ${brief.repos.join(' ')}`,
        ask: ASK.cancelBrief,
        target: brief.title,
        perform: (reason) =>
          write(() => api.cancelBrief(brief.id, reason), 'Job cancelled. Remaining tasks stopped.'),
      });
    }
    for (const task of failed) {
      list.push({
        id: `requeue-${task.id}`,
        group: 'Actions',
        subject: task.id,
        label: `${
          task.attempts >= task.maxAttempts ? 'Requeue with one more attempt' : 'Requeue'
        } - ${qualifiedKey(task.briefId, task.key)} ${task.title}`,
        icon: RefreshCw,
        search: `requeue retry failed ${qualifiedKey(task.briefId, task.key)} ${task.title}`,
        perform: () => write(() => api.requeueTask(task.id), 'Back in the queue.'),
      });
    }
    for (const task of taskRows) {
      if (task.status === 'done' || task.status === 'cancelled') continue;
      list.push({
        id: `cancel-${task.id}`,
        group: 'Actions',
        subject: task.id,
        label: `Cancel - ${qualifiedKey(task.briefId, task.key)} ${task.title}`,
        icon: CircleSlash,
        tone: 'danger',
        search: `cancel withdraw stop ${qualifiedKey(task.briefId, task.key)} ${task.title}`,
        ask: ASK.cancel,
        target: `${qualifiedKey(task.briefId, task.key)} ${task.title}`,
        perform: (reason) =>
          write(() => api.cancelTask(task.id, reason || undefined), 'Task cancelled.'),
      });
    }

    /**
     * Taking a device out of the fleet. Admin-only, like the Fleet page it comes
     * from, and it asks before it writes: the token goes with the row.
     *
     * Not offered for a device still holding unfinished work, which is the rule
     * the hub keeps and the Fleet row already shows. This list can be a sweep
     * behind, so the hub is still the one that decides; when it disagrees its
     * sentence lands in the palette's own error line.
     */
    const busyDevices = heldByWorker(taskRows);
    for (const device of devices.data ?? []) {
      if (busyDevices.get(device.id)?.length) continue;
      list.push({
        id: `retire-${device.id}`,
        group: 'Actions',
        label: `Retire device - ${device.name}`,
        detail: device.ownerEmail ?? undefined,
        icon: Server,
        tone: 'danger',
        search: `retire device worker machine laptop fleet ${device.name} ${device.ownerEmail ?? ''}`,
        ask: ASK['retire-worker'],
        target: device.name,
        perform: () => write(() => api.retireWorker(device.id), `${device.name} retired.`),
      });
    }

    list.push({
      id: 'new-brief',
      group: 'Actions',
      label: 'Open a new job',
      hint: 'C',
      icon: Plus,
      search: 'new job brief open file create compose work request',
      perform: () => {
        openCompose();
        reset();
      },
    });

    // The task board is a view on Jobs now, not a nav entry of its own
    // (issue #60), so it gets its own jump rather than riding the "Jobs"
    // entry below: somebody typing "board" or "steps" still lands on it in
    // one press, the same as before the rail's Steps row went away.
    list.push({
      id: 'task-board',
      group: 'Jump to',
      label: 'Task board',
      icon: ListTodo,
      search: 'task board steps queue running kanban',
      perform: () => go('/jobs', undefined, { board: true }),
    });

    // --- destinations -----------------------------------------------------
    const views: [string, string, LucideIcon, string][] = [
      ['/', 'Dashboard', LayoutDashboard, 'home dashboard inbox gates needs you'],
      ['/jobs', 'Jobs', ScrollText, 'jobs briefs plans'],
      ['/agents', 'Agents', Server, 'agents fleet workers devices machines claude'],
      ['/review', 'Review queue', GitPullRequest, 'review queue pull requests'],
      ['/me', 'Your account', UserRound, 'me account my devices my tasks settings theme density'],
      ['/about', 'How it works', BookOpen, 'about help how it works lifecycle keyboard'],
    ];
    if (session.isAdmin) {
      // One entry per admin page, still, because the palette is a search box
      // rather than a rail: somebody typing "audit" wants the audit log, not the
      // section that contains it.
      views.splice(4, 0, ['/admin', 'Admin', Settings, 'admin health capacity intake']);
      views.splice(5, 0, [
        '/admin/people',
        'People',
        Users,
        'people members invites limits access',
      ]);
      views.splice(6, 0, ['/admin/audit', 'Audit log', ScrollText, 'audit log record events']);
    }
    for (const [to, label, icon, keywords] of views) {
      list.push({
        id: `view-${to}`,
        group: 'Jump to',
        label,
        icon,
        search: `${label} ${keywords}`,
        perform: () => go(to),
      });
    }

    for (const project of projects.data ?? []) {
      list.push({
        id: `project-${project.repo}`,
        group: 'Projects',
        label: project.repo,
        detail: `${project.tasks.inFlight} in flight`,
        hint: project.inReview ? `${project.inReview} in review` : undefined,
        icon: LayoutGrid,
        search: `${project.repo} project repo`,
        perform: () => go('/project/$repo', { repo: project.repo }),
      });
    }

    for (const task of taskRows) {
      list.push({
        id: `task-${task.id}`,
        group: 'Tasks',
        subject: task.id,
        label: task.title,
        detail: `${qualifiedKey(task.briefId, task.key)} · ${labelForKind(task.status)} · ${
          task.repo
        }`,
        icon: ListTodo,
        search: `${qualifiedKey(task.briefId, task.key)} ${task.title} ${task.repo} ${
          task.status
        } ${task.assignedWorkerName ?? ''}`,
        perform: () => go('/tasks/$taskId', { taskId: task.id }),
      });
    }

    for (const brief of briefs.data ?? []) {
      list.push({
        id: `brief-${brief.id}`,
        group: 'Jobs',
        subject: brief.id,
        label: brief.title,
        detail: `${labelForKind(brief.status)} · ${brief.repos.join(', ') || 'no repo'}`,
        icon: ScrollText,
        search: `${brief.title} ${brief.repos.join(' ')} ${brief.status} ${brief.submittedBy} job brief`,
        perform: () => go('/jobs/$briefId', { briefId: brief.id }),
      });
    }

    /**
     * People and devices, which the palette could not find at all: "jordan" got
     * you nothing, not even Jordan. Neither has a page of its own, so the row is
     * named in the search string and `useFocusedRow` highlights it on arrival.
     */
    for (const person of people.data ?? []) {
      list.push({
        id: `person-${person.id}`,
        group: 'People',
        label: person.name || person.email,
        detail: person.isAdmin ? 'admin' : 'member',
        hint: person.devices.length
          ? `${person.devices.length} device${person.devices.length === 1 ? '' : 's'}`
          : undefined,
        icon: UserRound,
        search: `${person.name ?? ''} ${person.email} ${person.isAdmin ? 'admin' : 'member'} ${person.devices
          .map((device) => device.name)
          .join(' ')} person people member`,
        perform: () => go('/people', undefined, { focus: person.id }),
      });
    }

    for (const device of devices.data ?? []) {
      list.push({
        id: `device-${device.id}`,
        group: 'Devices',
        label: device.name,
        detail: device.ownerEmail ?? undefined,
        hint: presenceOf(device),
        icon: Laptop,
        search: `${device.name} ${device.hostname ?? ''} ${device.ownerEmail ?? ''} ${device.repos.join(
          ' ',
        )} ${presenceOf(device)} device worker machine laptop`,
        perform: () => go('/fleet', undefined, { focus: device.id }),
      });
    }

    list.push({
      id: 'theme',
      group: 'Display',
      label: `Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} theme`,
      icon: Moon,
      search: 'theme dark light appearance switch',
      perform: () => {
        setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
        close();
      },
    });
    list.push({
      id: 'density',
      group: 'Display',
      label: `Switch to ${density === 'compact' ? 'roomy' : 'compact'} density`,
      icon: Rows3,
      search: 'density compact roomy rows spacing',
      perform: () => {
        toggleDensity();
        close();
      },
    });

    return list;
  }, [
    briefs.data,
    close,
    density,
    devices.data,
    go,
    openCompose,
    paletteOpen,
    people.data,
    projects.data,
    reset,
    resolvedTheme,
    review.data,
    session.isAdmin,
    setTheme,
    tasks.data,
    toggleDensity,
    write,
  ]);

  /** Every subject that has at least one verb, so a row knows whether to say so. */
  const verbSubjects = useMemo(() => {
    const subjects = new Set<string>();
    for (const command of commands) {
      if (command.group === 'Actions' && command.subject) subjects.add(command.subject);
    }
    return subjects;
  }, [commands]);

  /**
   * The visible list: gates first while resting, one thing's verbs once you have
   * chosen a thing, ranked against everything once you type.
   */
  const results = useMemo(() => {
    const trimmed = query.trim();
    if (drilled) {
      const rows = commands.filter((command) => command.subject === drilled);
      // A brief can be both a gate row and a Briefs row; one of it is enough.
      const entity = rows.find((command) => command.group !== 'Actions');
      let verbs = rows.filter((command) => command.group === 'Actions');
      // Typing narrows the verbs rather than doing nothing, which is what an
      // editable field in front of a list has always promised. The thing itself
      // stays on screen whatever is typed: it is the context for the step, not a
      // result in it.
      if (trimmed) {
        verbs = verbs
          .map((command) => ({ command, match: fuzzyMatch(command.search, trimmed) }))
          .filter((row) => row.match !== null)
          .sort((a, b) => b.match!.score - a.match!.score)
          .map((row) => row.command);
      }
      if (entity) return [entity, ...verbs];
    }
    if (!trimmed) {
      const resting: Group[] = ['Waiting on you', 'Jump to', 'Projects', 'Display'];
      const picked = commands.filter((command) => resting.includes(command.group));
      const capped: Command[] = [];
      for (const group of GROUP_ORDER) {
        const inGroup = picked.filter((command) => command.group === group);
        capped.push(...(group === 'Waiting on you' ? inGroup.slice(0, 6) : inGroup));
      }
      // The composer is worth offering at rest too.
      const compose = commands.find((command) => command.id === 'new-brief');
      return compose ? [...capped, compose] : capped;
    }

    const scored: { command: Command; score: number; direct: boolean }[] = [];
    for (const command of commands) {
      const match = fuzzyMatch(command.search, trimmed);
      if (!match) continue;
      scored.push({ command, ...match });
    }
    /**
     * Once anything contains the query as it was typed, the rows that merely have
     * its letters lying around in order are noise: "failed" used to return a
     * draft task alongside the failures, because the letters f-a-i-l-e-d turn up
     * in order in most sentences. When nothing matches directly the loose pass is
     * all there is, and "flat" still finds "Fix the flaky auth test".
     */
    const answered = scored.some((row) => row.direct);
    const kept = answered ? scored.filter((row) => row.direct) : scored;
    kept.sort((a, b) => b.score - a.score);

    const perGroup = new Map<Group, number>();
    const out: Command[] = [];
    for (const { command } of kept) {
      const used = perGroup.get(command.group) ?? 0;
      if (used >= 6) continue;
      perGroup.set(command.group, used + 1);
      out.push(command);
      if (out.length >= 28) break;
    }
    // Keep the groups in their canonical order rather than in score order, so
    // the list does not reshuffle its sections on every keystroke.
    return GROUP_ORDER.flatMap((group) => out.filter((command) => command.group === group));
  }, [commands, drilled, query]);

  // Reset the cursor whenever the list changes shape under it.
  useLayoutEffect(() => {
    setActive((previous) => (previous >= results.length ? 0 : previous));
  }, [results.length]);

  // A thing that has gone takes its verb step with it: somebody else approving
  // the brief while this was open would otherwise leave escape bound to a step
  // with nothing in it. It leaves the same way escape does, search and all,
  // rather than dropping the query the step interrupted.
  useEffect(() => {
    if (drilled && !commands.some((command) => command.subject === drilled)) leaveVerbs();
  }, [commands, drilled, leaveVerbs]);

  // `pending` is a dependency on purpose: entering or leaving the reason step
  // swaps which input exists, and focus has to follow it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above.
  useEffect(() => {
    if (!paletteOpen) return;
    const raf = requestAnimationFrame(() => field.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [paletteOpen, pending]);

  useEffect(() => {
    if (!paletteOpen) return;
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, paletteOpen]);

  /**
   * Escape and cmd-K are bound to the window while the palette is open, not to
   * the input: leaving the reason step moves focus between two different fields,
   * and a key pressed during that hand-off used to reach neither of them.
   */
  useEffect(() => {
    if (!paletteOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (pending) {
        setPending(null);
        setValue('');
        setError(null);
        return;
      }
      if (drilled) {
        leaveVerbs();
        return;
      }
      close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close, drilled, leaveVerbs, paletteOpen, pending]);

  // The page behind must not scroll while the palette is over it. Counted, since
  // picking a task from here opens the detail sheet on top: `scroll-lock.ts`.
  useBodyScrollLock(paletteOpen);

  const choose = useCallback((command: Command) => {
    if (command.ask) {
      setPending(command);
      setValue('');
      setError(null);
      return;
    }
    void command.perform('');
  }, []);

  if (!paletteOpen) return null;

  const config = pending?.ask;
  /**
   * What the verbs on screen belong to. Null when the thing has gone -- somebody
   * else approved the brief while this was open -- and then the list is the
   * ordinary one again rather than a set of verbs with nothing to act on.
   */
  const drilledInto = drilled
    ? (results.find((command) => command.subject === drilled && command.group !== 'Actions') ??
      null)
    : null;
  /**
   * Whether the row under the cursor is a thing with verbs. The footer says so
   * only when it is true, because a hint for a key that does nothing on the row
   * you are on is worse than no hint at all.
   */
  const activeHasVerbs = Boolean(
    !drilled && results[active]?.subject && verbSubjects.has(results[active]!.subject!),
  );
  // The same rule `AskBox` enforces in a row: a verb whose note is required
  // cannot be confirmed empty. Without this the palette hands the hub a write it
  // answers 422 to, and the person gets a toast instead of an answer.
  const missing = Boolean(config?.required) && !value.trim();

  const onKeyDown = (event: ReactKeyboardEvent) => {
    // Escape and cmd-K are handled on the window; see the effect above.
    if (pending || event.key === 'Escape') return;
    if (event.key === 'ArrowDown' || (event.key === 'n' && event.ctrlKey)) {
      event.preventDefault();
      setActive((previous) => (results.length ? (previous + 1) % results.length : 0));
      return;
    }
    if (event.key === 'ArrowUp' || (event.key === 'p' && event.ctrlKey)) {
      event.preventDefault();
      setActive((previous) =>
        results.length ? (previous - 1 + results.length) % results.length : 0,
      );
      return;
    }
    /**
     * Right opens a thing's verbs, left closes them again, and cmd-Enter does the
     * same as right for a hand that is already on Enter. Both arrows only act
     * when the caret is at that end of the query, so typing and correcting a
     * search still moves the caret the way every other text field does.
     */
    const caret = event.currentTarget instanceof HTMLInputElement ? event.currentTarget : null;
    const command = results[active];
    if (
      (event.key === 'ArrowRight' && caret?.selectionStart === query.length) ||
      (event.key === 'Enter' && (event.metaKey || event.ctrlKey))
    ) {
      if (command?.subject && verbSubjects.has(command.subject) && drilled !== command.subject) {
        event.preventDefault();
        enterVerbs(command.subject, query);
      }
      return;
    }
    if (event.key === 'ArrowLeft' && drilled && caret?.selectionStart === 0) {
      event.preventDefault();
      leaveVerbs();
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActive(Math.max(0, results.length - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (command) choose(command);
    }
  };

  let lastGroup: Group | null = null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3 sm:p-4">
      <button
        type="button"
        aria-label="Close the command palette"
        onClick={close}
        className="dy-scrim-in absolute inset-0"
        style={{ background: 'var(--dy-overlay)' }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className={cn(
          'bg-pop dy-pop-in relative mt-[8vh] w-full max-w-[38rem] overflow-hidden',
          'rounded-xl border shadow-pop',
        )}
      >
        {pending && config ? (
          <div className="border-b">
            <div className="bg-sunken flex items-center gap-2 px-3 py-2">
              <span
                className={cn(
                  'flex h-[18px] shrink-0 items-center rounded-sm px-1.5 text-2xs font-medium',
                  config.tone === 'danger' ? 'bg-bad-bg text-bad' : 'bg-gate-bg text-gate',
                )}
              >
                {config.confirm}
              </span>
              <span className="text-fg-2 min-w-0 truncate text-xs">
                {pending.target ?? pending.label}
              </span>
              <span className="text-fg-3 ml-auto shrink-0 text-2xs">esc to go back</span>
            </div>
            <p className="text-fg-2 px-3 pt-2 text-xs">{config.question}</p>
            <form
              className="flex items-center gap-2 px-3 pt-1.5 pb-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (missing) return;
                void pending.perform(value.trim());
              }}
            >
              {config.field ? (
                <input
                  ref={field}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={config.field}
                  maxLength={500}
                  required={config.required}
                  className="placeholder:text-fg-3 h-8 min-w-0 flex-1 border-b border-line-2 bg-transparent text-sm outline-none focus:border-accent-line"
                />
              ) : (
                <input
                  ref={field}
                  onKeyDown={onKeyDown}
                  readOnly
                  className="sr-only"
                  aria-label={config.question}
                />
              )}
              <button
                type="submit"
                disabled={busy || missing}
                className={cn(
                  'flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium',
                  'disabled:opacity-50',
                  config.tone === 'danger'
                    ? 'bg-destructive text-white'
                    : 'bg-gate text-primary-foreground',
                )}
              >
                {busy ? 'Working…' : config.confirm}
                <CornerDownLeft className="size-3" aria-hidden />
              </button>
            </form>
            {error ? (
              <p className="text-bad px-3 pb-3 text-xs" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        ) : (
          <>
            {drilledInto ? (
              <div className="bg-sunken flex items-center gap-2 border-b px-3 py-2">
                <span className="bg-raised text-fg-2 flex h-[18px] shrink-0 items-center rounded-sm px-1.5 text-2xs font-medium">
                  Verbs
                </span>
                <span className="text-fg-2 min-w-0 truncate text-xs">{drilledInto.label}</span>
                <span className="text-fg-3 ml-auto shrink-0 text-2xs">esc to go back</span>
              </div>
            ) : null}
            <div className="flex h-12 items-center gap-2.5 border-b px-3">
              <Search className="text-fg-3 size-[15px] shrink-0" aria-hidden />
              <input
                ref={field}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="Search views, tasks, jobs, or type a verb"
                aria-label="Search views, tasks, jobs and actions"
                aria-controls="dy-command-list"
                aria-activedescendant={results[active] ? `dy-command-${active}` : undefined}
                className="placeholder:text-fg-3 min-w-0 flex-1 bg-transparent text-md outline-none"
              />
              <kbd className="dy-kbd shrink-0">esc</kbd>
            </div>

            <div
              id="dy-command-list"
              ref={listRef}
              role="listbox"
              aria-label="Results"
              className="dy-scroll max-h-[min(26rem,58vh)] overflow-y-auto py-1"
            >
              {results.length ? (
                results.map((command, index) => {
                  const header = command.group !== lastGroup ? command.group : null;
                  lastGroup = command.group;
                  return (
                    <div key={command.id} data-index={index}>
                      {header ? (
                        <div className="dy-eyebrow px-3 pt-2 pb-1">
                          {header === 'Waiting on you' && command.tone === 'gate' ? (
                            <span className="text-gate">{header}</span>
                          ) : (
                            header
                          )}
                        </div>
                      ) : null}
                      <Row
                        id={`dy-command-${index}`}
                        command={command}
                        active={index === active}
                        hasVerbs={
                          !drilled && Boolean(command.subject && verbSubjects.has(command.subject))
                        }
                        onHover={() => setActive(index)}
                        onPick={() => choose(command)}
                      />
                    </div>
                  );
                })
              ) : (
                <p className="text-fg-3 px-3 py-8 text-center text-sm">
                  Nothing matches “{query.trim()}”. Try a task key, a repo, or a verb like
                  “approve”.
                </p>
              )}
            </div>

            <div className="bg-sunken flex h-9 items-center gap-3 border-t px-3">
              <Legend>
                <kbd className="dy-kbd">↑</kbd>
                <kbd className="dy-kbd">↓</kbd>
                move
              </Legend>
              <Legend>
                <kbd className="dy-kbd">↵</kbd>
                run
              </Legend>
              {activeHasVerbs ? (
                <Legend className="hidden sm:flex">
                  <kbd className="dy-kbd">→</kbd>
                  verbs
                </Legend>
              ) : null}
              <Legend className="hidden md:flex">
                <kbd className="dy-kbd">g</kbd>
                <ArrowRight className="size-2.5" aria-hidden />
                <kbd className="dy-kbd">t</kbd>
                jump
              </Legend>
              <span className="text-fg-3 ml-auto text-2xs">
                {results.length} of {commands.length}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
